import React, { useState, memo } from 'react';
import { BookOpen, Mic, Pencil, Upload, Plus, Clock, Search, Play, Tag, TrendingUp, Grid, List, Calendar } from 'lucide-react';
import { StoryCard, Story } from './StoryCard';
import { StoryModal } from './StoryModal';
import { StartRecordingModal } from './StartRecordingModal';
import { EmptyState } from './EmptyState';
import { FilterBar } from './FilterBar';
import { StoryCardSkeleton } from './SkeletonLoader';
import { BulkActionsBar } from './BulkActionsBar';
import { BulkTagModal } from './BulkTagModal';
import { ConfirmDialog } from './ConfirmDialog';
const sampleStories: Story[] = [{
  id: 1,
  thumbnail: 'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=800&h=600&fit=crop',
  duration: '12:45',
  title: 'How Mom and Dad Met',
  description: 'The story of how my parents met at a college dance in 1985, their first date at the diner, and the moment Dad knew she was the one.',
  author: 'Sarah Mitchell',
  date: 'December 18, 2024',
  tags: ['Family', 'Love Story', 'Memories'],
  hasAISummary: true
}, {
  id: 2,
  thumbnail: 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=800&h=600&fit=crop',
  duration: '8:23',
  title: "Grandma's Apple Pie Recipe",
  description: "Learning to make Grandma Rose's famous apple pie from scratch. She shares the secret ingredient and memories of baking with her own mother.",
  author: 'Emily Chen',
  date: 'December 15, 2024',
  tags: ['Recipes', 'Family', 'Traditions'],
  hasAISummary: true
}, {
  id: 3,
  thumbnail: 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=800&h=600&fit=crop',
  duration: '15:30',
  title: 'Road Trip to Yellowstone',
  description: "Our family's unforgettable road trip through Yellowstone National Park. Bears, geysers, and the time we got lost trying to find Old Faithful.",
  author: 'Michael Rodriguez',
  date: 'December 10, 2024',
  tags: ['Travel', 'Adventure', 'Family'],
  hasAISummary: true
}, {
  id: 4,
  thumbnail: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=600&fit=crop',
  duration: '10:15',
  title: 'Summer at Lake Tahoe',
  description: 'Memories of our childhood summers at the lake house - fishing with Grandpa, swimming until sunset, and campfire stories under the stars.',
  author: 'James Patterson',
  date: 'December 5, 2024',
  tags: ['Childhood', 'Summer', 'Memories'],
  hasAISummary: false
}, {
  id: 5,
  thumbnail: 'https://images.unsplash.com/photo-1511895426328-dc8714191300?w=800&h=600&fit=crop',
  duration: '6:42',
  title: 'The Day We Adopted Max',
  description: 'How our golden retriever Max became part of the family. From the shelter to his first day home, and all the chaos and love that followed.',
  author: 'Lisa Thompson',
  date: 'November 28, 2024',
  tags: ['Pets', 'Family', 'Memories'],
  hasAISummary: false
}, {
  id: 6,
  thumbnail: 'https://images.unsplash.com/photo-1464207687429-7505649dae38?w=800&h=600&fit=crop',
  duration: '18:20',
  title: "Dad's Navy Stories",
  description: 'Dad shares his experiences serving in the Navy during the 1970s. Tales of life at sea, ports around the world, and the friendships that lasted a lifetime.',
  author: 'Robert Williams',
  date: 'November 22, 2024',
  tags: ['Military', 'History', 'Family'],
  hasAISummary: true
}, {
  id: 7,
  thumbnail: 'https://images.unsplash.com/photo-1519340241574-2cec6aef0c01?w=800&h=600&fit=crop',
  duration: '9:55',
  title: 'Christmas Traditions',
  description: "Our family's Christmas traditions passed down through generations - from decorating the tree to Mom's famous sugar cookies and midnight mass.",
  author: 'Maria Garcia',
  date: 'November 18, 2024',
  tags: ['Traditions', 'Holidays', 'Family'],
  hasAISummary: true
}, {
  id: 8,
  thumbnail: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=800&h=600&fit=crop',
  duration: '14:10',
  title: 'Growing Up in Brooklyn',
  description: 'Stories from my childhood in 1960s Brooklyn - stickball in the streets, corner stores, and the tight-knit neighborhood where everyone knew your name.',
  author: 'Frank Russo',
  date: 'November 12, 2024',
  tags: ['Childhood', 'History', 'Memories'],
  hasAISummary: true
}];
type StoriesPageProps = {
  onStartRecording?: () => void;
  onViewStory?: (storyId: number) => void;
};
type ViewMode = 'grid' | 'list';
type SortOption = 'date' | 'title' | 'duration';
export function StoriesPage({
  onStartRecording,
  onViewStory
}: StoriesPageProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<SortOption>('date');
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const availableTags = ['Family', 'Childhood', 'Travel', 'Recipes', 'Traditions', 'Memories', 'Adventure', 'History', 'Military', 'Pets', 'Summer'];
  // Filter and sort stories
  let filteredStories = sampleStories.filter(story => {
    const matchesSearch = story.title.toLowerCase().includes(searchQuery.toLowerCase()) || story.description.toLowerCase().includes(searchQuery.toLowerCase()) || story.author.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTags = selectedTags.length === 0 || story.tags && selectedTags.some(tag => story.tags?.includes(tag));
    return matchesSearch && matchesTags;
  });
  // Sort stories
  filteredStories = [...filteredStories].sort((a, b) => {
    switch (sortBy) {
      case 'title':
        return a.title.localeCompare(b.title);
      case 'duration':
        return a.duration.localeCompare(b.duration);
      case 'date':
      default:
        return new Date(b.date).getTime() - new Date(a.date).getTime();
    }
  });
  // Assign variable heights for masonry effect
  const storiesWithHeights = filteredStories.map((story, index) => {
    const heights = ['short', 'medium', 'tall'];
    const height = heights[index % 3];
    return {
      ...story,
      height
    };
  });
  return <div className="flex-1 bg-gray-100 min-h-screen" data-id="element-2189">
      <div className="px-8 py-8" data-id="element-2190">
        {/* Header with 3 Action Buttons */}
        <div className="flex items-center justify-between mb-8" data-id="element-2191">
          <div data-id="element-2192">
            <h1 className="text-4xl font-bold text-gray-900 mb-2" data-id="element-2193">Stories</h1>
            <p className="text-gray-600 text-lg" data-id="element-2194">
              Create and explore your memories
            </p>
          </div>

          {/* 3 Separate Action Buttons - Always Visible */}
          <div className="flex gap-3" data-id="element-2195">
            <button onClick={onStartRecording} data-tour="record-button" className="flex items-center gap-2 px-6 py-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all shadow-lg hover:shadow-xl" data-id="element-2196">
              <Mic className="w-5 h-5" data-id="element-2197" />
              Record
            </button>

            <button className="flex items-center gap-2 px-6 py-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-bold rounded-xl hover:from-blue-600 hover:to-cyan-600 transition-all shadow-lg hover:shadow-xl" data-id="element-2198">
              <Pencil className="w-5 h-5" data-id="element-2199" />
              Journal
            </button>

            <button className="flex items-center gap-2 px-6 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all shadow-lg hover:shadow-xl" data-id="element-2200">
              <Upload className="w-5 h-5" data-id="element-2201" />
              Upload
            </button>
          </div>
        </div>

        {/* Search, Filter, View Switcher Bar - Same as Creations */}
        <div className="bg-white rounded-2xl p-4 shadow-sm mb-6" data-id="element-2202">
          <div className="flex items-center gap-4" data-id="element-2203">
            {/* Search */}
            <div className="flex-1 relative" data-id="element-2204">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-2205" />
              <input type="text" placeholder="Search stories..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-green-400 focus:outline-none transition-colors" data-id="element-2206" />
            </div>

            {/* Sort Dropdown */}
            <select value={sortBy} onChange={e => setSortBy(e.target.value as SortOption)} className="px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-green-400 focus:outline-none transition-colors font-medium text-gray-700" data-id="element-2207">
              <option value="date" data-id="element-2208">Sort by Date</option>
              <option value="title" data-id="element-2209">Sort by Title</option>
              <option value="duration" data-id="element-2210">Sort by Duration</option>
            </select>

            {/* View Mode Toggle */}
            <div className="flex items-center gap-2 bg-gray-50 p-1 rounded-xl" data-id="element-2211">
              <button onClick={() => setViewMode('grid')} className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-white text-green-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} title="Grid view" data-id="element-2212">
                <Grid className="w-5 h-5" data-id="element-2213" />
              </button>
              <button onClick={() => setViewMode('list')} className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-white text-green-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} title="List view" data-id="element-2214">
                <List className="w-5 h-5" data-id="element-2215" />
              </button>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6" data-id="element-2216">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-2217">
            {filteredStories.length !== sampleStories.length ? `${filteredStories.length} of ${sampleStories.length} Stories` : `${sampleStories.length} Stories`}
          </h2>
        </div>

        {/* Stories Grid or Empty State */}
        {isLoading ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2218">
            <StoryCardSkeleton data-id="element-2219" />
            <StoryCardSkeleton data-id="element-2220" />
            <StoryCardSkeleton data-id="element-2221" />
          </div> : filteredStories.length === 0 ? searchQuery || selectedTags.length > 0 ? <EmptyState icon={Search} title="No stories found" description={`No stories match your current filters. Try adjusting your search or filters.`} actionLabel="Clear Filters" onAction={() => {
        setSearchQuery('');
        setSelectedTags([]);
      }} data-id="element-2222" /> : <EmptyState icon={Mic} title="No stories yet" description="Start recording your first family story to preserve precious memories for generations to come." actionLabel="Record Your First Story" onAction={onStartRecording} data-id="element-2223" /> : viewMode === 'grid' ? <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6" data-id="element-2224">
            {storiesWithHeights.map(story => {
          const heightClass = story.height === 'short' ? 'h-64' : story.height === 'tall' ? 'h-96' : 'h-80';
          return <div key={story.id} onClick={() => onViewStory?.(story.id)} className="group break-inside-avoid" data-id="element-2225">
                  <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all cursor-pointer hover:-translate-y-1" data-id="element-2226">
                    {/* Thumbnail */}
                    <div className={`relative ${heightClass} overflow-hidden`} data-id="element-2227">
                      <img src={story.thumbnail} alt={story.title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" data-id="element-2228" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" data-id="element-2229"></div>

                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" data-id="element-2230">
                        <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center" data-id="element-2231">
                          <Play className="w-8 h-8 text-gray-900 ml-1" data-id="element-2232" />
                        </div>
                      </div>

                      <div className="absolute bottom-4 left-4 right-4" data-id="element-2233">
                        <h3 className="text-2xl font-bold text-white mb-2 drop-shadow-lg" data-id="element-2234">
                          {story.title}
                        </h3>
                        <div className="flex items-center gap-2 flex-wrap" data-id="element-2235">
                          <div className="px-2.5 py-1 bg-black/75 text-white text-xs font-medium rounded backdrop-blur-sm" data-id="element-2236">
                            {story.duration}
                          </div>
                          {story.tags?.slice(0, 2).map(tag => <div key={tag} className="px-2.5 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-medium rounded" data-id="element-2237">
                              {tag}
                            </div>)}
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-5" data-id="element-2238">
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2" data-id="element-2239">
                        {story.description}
                      </p>

                      <div className="flex items-center justify-between text-sm text-gray-500" data-id="element-2240">
                        <div className="flex items-center gap-2" data-id="element-2241">
                          <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center" data-id="element-2242">
                            <span className="text-green-600 font-semibold text-xs" data-id="element-2243">
                              {story.author.charAt(0)}
                            </span>
                          </div>
                          <span data-id="element-2244">{story.author}</span>
                        </div>
                        <span data-id="element-2245">
                          {new Date(story.date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    })}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>;
        })}
          </div> : <div className="space-y-4" data-id="element-2246">
            {filteredStories.map(story => <div key={story.id} onClick={() => onViewStory?.(story.id)} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all cursor-pointer" data-id="element-2247">
                <div className="flex gap-6" data-id="element-2248">
                  {/* Thumbnail */}
                  <img src={story.thumbnail} alt={story.title} className="w-32 h-32 rounded-xl object-cover flex-shrink-0" data-id="element-2249" />

                  {/* Content */}
                  <div className="flex-1 min-w-0" data-id="element-2250">
                    <div className="flex items-start justify-between mb-2" data-id="element-2251">
                      <div data-id="element-2252">
                        <h3 className="text-2xl font-bold text-gray-900 mb-1" data-id="element-2253">
                          {story.title}
                        </h3>
                        <div className="flex items-center gap-3 text-sm text-gray-600" data-id="element-2254">
                          <div className="flex items-center gap-1" data-id="element-2255">
                            <Calendar className="w-4 h-4" data-id="element-2256" />
                            {story.date}
                          </div>
                          <span data-id="element-2257">•</span>
                          <div className="flex items-center gap-1" data-id="element-2258">
                            <Clock className="w-4 h-4" data-id="element-2259" />
                            {story.duration}
                          </div>
                        </div>
                      </div>
                    </div>

                    <p className="text-gray-700 mb-3 line-clamp-2" data-id="element-2260">
                      {story.description}
                    </p>

                    <div className="flex items-center gap-2" data-id="element-2261">
                      {story.tags?.map(tag => <span key={tag} className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium" data-id="element-2262">
                          <Tag className="w-3 h-3" data-id="element-2263" />
                          {tag}
                        </span>)}
                    </div>
                  </div>
                </div>
              </div>)}
          </div>}
      </div>
    </div>;
}